import { createContext, useContext } from 'react'
import type { ApiFailure, ApiResult, Schema } from '@/api/client'

export type Tenant = Schema<'TenantOut'>
export type Membership = Schema<'MembershipOut'>
export type TenantMembership = Schema<'TenantMembershipOut'>
export type Role = Schema<'Role'>

export interface TenantValue {
  status: 'loading' | 'ready' | 'error'
  failure: ApiFailure | null
  /** Every organisation this user belongs to — what fills the switcher. */
  memberships: TenantMembership[]
  /** The one the *session* is in. Null until an organisation is selected. */
  active: TenantMembership | null
  /**
   * Switch the session's organisation. Resolves once the switch has landed.
   *
   * The real failure and not a boolean: a boolean has exactly one reason in it,
   * so every way this can fail — a network drop, a 5xx, a 429,
   * `TENANT_UNAVAILABLE` — reached the user as "you are no longer a member of
   * that organisation", which is both wrong and unactionable (WEB-9). The
   * caller decides which failures deserve that sentence.
   */
  select: (tenantId: string) => Promise<ApiResult<void>>
  /**
   * Add an organisation the app has just been handed, without waiting for a
   * refetch. Creating one and accepting an invitation both return the tenant
   * *and* select it on the session, so at that moment both halves are known —
   * and navigating into the shell before a `reload()` lands would bounce off
   * `RequireTenant` and back to the picker.
   */
  adopt: (row: TenantMembership) => void
  reload: () => void
}

export const TenantContext = createContext<TenantValue | null>(null)

export function useTenants(): TenantValue {
  const value = useContext(TenantContext)
  if (!value) throw new Error('useTenants was called outside <TenantProvider>')
  return value
}

/**
 * The active organisation, for screens the tenant guard has already vetted.
 *
 * Throws rather than returning null for the same reason as `useCurrentUser`:
 * the router has made the null case unreachable, and a screen that renders with
 * an empty organisation name is a wiring bug that should be loud.
 */
export function useActiveTenant(): { tenant: Tenant; membership: Membership; role: Role } {
  const { active } = useTenants()
  if (!active) throw new Error('useActiveTenant was called outside a tenant-scoped route')
  return { tenant: active.tenant, membership: active.membership, role: active.membership.role }
}

/**
 * The permission matrix, mirrored from `api/app/tenancy/roles.py`.
 *
 * A mirror, not a source of truth — the API refuses the request regardless, and
 * this exists only so the UI does not offer a button that is going to 403. It
 * is deliberately *not* generated: OpenAPI describes shapes, not authorisation,
 * so there is nothing to generate it from. The rule that keeps it honest is
 * that every entry corresponds to a `Permission` on the server, and the
 * server's refusal is what actually enforces it.
 */
export const ROLE_CAPABILITIES: Record<Role, ReadonlySet<string>> = {
  owner: new Set([
    'tenant:read',
    'tenant:update',
    'members:read',
    'members:invite',
    'members:remove',
    'members:role',
    'notifications:manage',
    // S-B12. `tenant:delete` is owner-only on the server too, and for the
    // reason stated there: deleting an organisation destroys every other
    // member's work.
    'audit:read',
    'data:export',
    'suppression:manage',
    'tenant:delete',
    // S-B6. `billing:manage` is owner-only on the server too, and S-D3's
    // approved role matrix is what decides it: *Billing & cancellation* has
    // a tick in the Owner column and a dash in every other one.
    'billing:read',
    'billing:manage',
    // S-B7. Owner *and admin*, unlike the two above: S-D3's matrix ticks
    // *Change budget caps* in both columns, because capping what automation may
    // spend is operating the organisation rather than buying it.
    'budget:manage',
    // S-B19. Reading the inbox is every role's; **replying stops at admin**, and
    // the server draws the same line for the reason it states there — a reply is
    // an outbound message to somebody outside the organisation, in its name,
    // under §8's obligations. This set is the screen's half of that: a member
    // sees the conversation and no composer, rather than a composer that fails.
    'inbox:read',
    'inbox:reply',
    // S-G5. Owner *and admin* — `PERMISSIONS[Role.ADMIN]` on the server holds
    // it, on S-B27's argument that `member` is S-D3's Editor and Viewer
    // collapsed, so the line has to go at admin. Added when the publish screen
    // arrived: pressing *Publish* and *Roll back* are `content:approve` on
    // `app/v1/seo_publish.py`, and until now no screen asked this table about
    // it — a permission the server enforces and the client had never heard of.
    'content:approve',
    // S-G6. Owner *and admin* again, mirroring `PERMISSIONS[Role.ADMIN]`. The
    // content pipeline is the first screen to ask for it: *Write it*,
    // *Rewrite* and *Run the gate again* are all `discovery:manage` on
    // `app/v1/seo_content.py`, and that permission's own note gives the reason
    // it is not `tenant:update` — every route holding it spends money against
    // the organisation's budget cap.
    'discovery:manage',
    // S-B20. Owner *and admin*, mirroring `PERMISSIONS[Role.ADMIN]`. The
    // outreach module's management half: moving a lead through the pipeline,
    // overruling a reply's category, and editing the brief, the ladder and the
    // daily cap. It stops where `inbox:reply` stops and for the same argument
    // taken one step further — a reply is one message to one stranger in the
    // organisation's name, and a brief decides what goes to *every* prospect and
    // how many times.
    'outreach:manage',
  ]),
  admin: new Set([
    'tenant:read',
    'tenant:update',
    'members:read',
    'members:invite',
    'members:remove',
    'notifications:manage',
    'audit:read',
    'data:export',
    'suppression:manage',
    // Reading only. An accountant asks for a GST invoice, and one only the
    // founder can fetch is one that holds up a filing — the same argument
    // S-B12 made for `data:export`.
    'billing:read',
    // S-B7. See the owner row: the design ticks this one for an admin.
    'budget:manage',
    // S-B19. See the owner row.
    'inbox:read',
    'inbox:reply',
    // S-G5. See the owner row: the server ticks this one for an admin too.
    'content:approve',
    // S-G6. See the owner row: the server ticks this one for an admin too.
    'discovery:manage',
    // S-B20. Owner *and admin*, mirroring `PERMISSIONS[Role.ADMIN]`. The
    // outreach module's management half: moving a lead through the pipeline,
    // overruling a reply's category, and editing the brief, the ladder and the
    // daily cap. It stops where `inbox:reply` stops and for the same argument
    // taken one step further — a reply is one message to one stranger in the
    // organisation's name, and a brief decides what goes to *every* prospect and
    // how many times.
    'outreach:manage',
  ]),
  member: new Set(['tenant:read', 'members:read', 'inbox:read']),
}

export function may(role: Role, permission: string): boolean {
  return ROLE_CAPABILITIES[role].has(permission)
}
